<template>
    <div>
        <section class="provider-search-bar">
            <a v-if="backButton" class="btn-back" :href="backButton.url">{{ backButton.text }}</a>

            <provider-search />
        </section>
        <div class="provider-details">
            <header>
                <div class="elementor-background-overlay"></div>
                <div class="details">
                    <div class="image">
                        <img v-if="provider.alt_avatar" :src="provider.alt_avatar" />
                        <img v-else="provider.avatar" :src="provider.avatar" />
                    </div>

                    <div class="text">
                        <h1>{{ provider.name }}</h1>
                        <div class="flex">
                            <div class="left-details">
                                <template v-if="provider.position">
                                    <strong>{{ provider.position }}</strong>
                                    <br />
                                </template>
                                <span class="services primary" v-if="provider.servicesWithCredentialed"><i>{{ provider.servicesWithCredentialed }}</i></span>
                                <span class="services" v-if="provider.servicesWithoutCredentialedAndNotInCredentialed">(<i>{{ provider.servicesWithoutCredentialedAndNotInCredentialed }}</i>)</span>

                                <div v-if="provider.tiers.includes('qmg') || provider.tiers.includes('qcipn')" class="provider-logos">
                                    <div v-if="provider.tiers.includes('qmg')" class="logo">
                                        <div class="logo-wrapper">
                                            <img class="qmg-logo" src="/wp-content/uploads/qhs-dir-api/qmg-logo.jpg">
                                            <div class="tooltip">
                                                <i class="far fa-question-circle"></i>
                                                <div class="tooltip-text">The Queen's University Medical Group (QUMG) is QHS' academic multi-specialty medical group.  QUMG is the preeminent academic medical group of the Pacific - leading the delivery of high quality, compassionate patient care and continually advancing education and research.  We provide quality patient care services throughout the State of Hawaii.  This includes urgent and ambulatory care services at clinics throughout the state and acute care services at The Queen's Medical Center's Punchbowl and West campuses.</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="provider.tiers.includes('qcipn')" class="logo">
                                        <div class="logo-wrapper">
                                            <img class="qcipn-logo" src="/wp-content/uploads/qhs-dir-api/qcipn-logo.jpg">
                                            <div class="tooltip">
                                                <i class="far fa-question-circle"></i>
                                                <div class="tooltip-text">The Queen's Clinically Integrated Physician Network is a collaboration between The Queen's Health System, community physicians, and Physician Organizations with a shared goal of providing high quality care, increased efficiency, and optimal patient health.</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div class="right-details">
                                <provider-features v-if="provider.tiers.includes('qcipn') || provider.tiers.includes('qmg') || provider.tiers.includes('enhanced_profile')"  :provider="provider" />
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <ol class="breadcrumb">
                <li>
                    <a href="https://www.queens.org/find-a-doctor/">Find a Doctor</a>
                </li>
                <li>></li>
                <li>
                    <a :href="`https://www.queens.org/providers/${provider.id}/${provider.name_for_url}/`">{{ provider.name }}</a>
                </li>
            </ol>

            <section class="provider-info">
                <div class="left-rail" v-if="provider.video_url || provider.bio || provider.location_count != 0" :class="{full: !(provider.education && provider.education.length) && !(provider.board_certification && provider.board_certification.length) && !(provider.insurances && provider.insurances.length)}">
                    <div class="video-bio-link" v-if="provider.video_url || provider.bio">
                        <div v-if="provider.video_url" class="video-embed-wrapper">
                            <iframe v-if="/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(provider.video_url)"
                                :src="`https://freshpaint-video.com/43d4fb8c-c50b-4b88-acc0-81faf7886114/${provider.video_url}`"
                                width="640"
                                height="360"
                                frameborder="0"
                                title="Freshpaint Video Player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowfullscreen>
                            </iframe>

                            <iframe v-else
                                :src="`${provider.video_url}`"
                                width="640"
                                height="360"
                                frameborder="0"
                                title="Freshpaint Video Player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowfullscreen>
                            </iframe>
                        </div>
                        <p v-html="provider.bio"></p>
                        <p v-if="provider.additional_information_url">
                            <a :href="provider.additional_information_url" class="additional-information" target="_blank">Additional Information</a>
                        </p>
                    </div>

                    <template v-if="provider.location_count != 0">
                        <div class="locations">
                            <h3>Where I work</h3>
                            <div class="alert" v-if="provider.tiers.includes('mgh')">
                                Please Note: This Provider has privileges to practice NHCH and MGH, but do not take appointments directly from patients.
                            </div>

                            <div v-for="(group, index) in provider.locations" :key="index" class="location">
                                <div v-if="group.location" class="border">
                                    <h4>{{ group.location.building_name ? group.location.building_name : 'Location' }}</h4>

                                    <div class="content">
                                        <div class="left">
                                        <p class="credentialed-services" v-if="group.credentialedServices.length">
                                            {{ group.credentialedServices.join(', ') }}</strong>
                                        </p>

                                        <p class="non-credentialed-services" v-if="group.nonCredentialedServices.length">
                                            ({{ group.nonCredentialedServices.join(', ') }})
                                        </p>

                                        <p v-if="group.accepting_new_patients"><i>Accepting New Patients</i></p>

                                        <p v-if="group.location.name">{{ group.location.name }}</p>

                                        <template v-if="group.location.address.length && group.location.address.length">
                                            <p v-html="withLineBreaks(group.location.address)"></p>
                                        </template>

                                        <p v-if="group.location_phone && group.location_phone != 0 && !provider.tiers.includes('mgh')">Tel: <a :href="`tel:${group.location_phone_clickable}`">{{ group.location_phone }}</a></p>

                                        <p v-if="group.location_fax && group.location_fax != 0 && !provider.tiers.includes('mgh')">Fax: {{ group.location_fax }}</p>
                                    </div>
                                    <div class="right">
                                        <p>
                                            <a class="btn-outlined" :href="`http://maps.google.com/?q=${group.location.latitude},${group.location.longitude}`" target="_blank" role="button">
                                                <i aria-hidden="true" class="fas fa-map-marked-alt"></i>
                        					    <span>Get Directions</span>
                        				    </a>
                                        </p>
                                    </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="map">
                            <div id="map" style="width: 100%; height: 500px;"></div>
                        </div>

                        <div v-if="campusMap">
                            <img src="https://www.queens.org/wp-content/uploads/QMC-Campus-Map-2023-1024x962.jpg"/>
                        </div>
                    
                    </template>
                </div>

                <div v-if="(provider.education && provider.education.length) || (provider.board_certification && provider.board_certification.length) || (provider.insurances && provider.insurances.length)" class="right-rail" :class="{full: !provider.video_url && !provider.bio && provider.location_count == 0}">

                <div class="education" v-if="provider.education && provider.education.length">
                    <h3>Education</h3>
                    <div class="wrapper">
                        <div class="group" v-if="medicalEducation.length">
                            <h4>Medical Education</h4>
                            <div class="item" v-if="education.educationName || education.educationSubject" v-for="(education, index) in medicalEducation" :key="index">
                                <div class="item-1" v-if="education.educationName">
                                    {{ education.educationName }}
                                </div>

                                <div class="item-2" v-if="education.educationSubject">
                                    {{ education.educationSubject }}
                                </div>
                            </div>
                        </div>

                        <div class="group" v-if="internshipEducation.length">
                            <h4>Internship</h4>
                            <div class="item" v-for="(education, index) in internshipEducation" :key="index" v-if="education.educationType == 'Internship'">
                                <div class="item-1" v-if="education.educationName">
                                    {{ education.educationName }}
                                </div>

                                <div class="item-2" v-if="education.educationSubject">
                                    {{ education.educationSubject }}
                                </div>
                            </div>
                        </div>

                        <div class="group" v-if="residencyEducation.length">
                            <h4>Residency</h4>
                            <div class="item" v-for="(education, index) in residencyEducation" :key="index" v-if="education.educationType == 'Residency'">
                                <div class="item-1" v-if="education.educationName">
                                    {{ education.educationName }}
                                </div>

                                <div class="item-2" v-if="education.educationSubject">
                                    {{ education.educationSubject }}
                                </div>
                            </div>
                        </div>

                        <div class="group" v-if="fellowshipEducation.length">
                            <h4>Fellowship</h4>
                            <div class="item" v-for="(education, index) in fellowshipEducation" :key="index" v-if="education.educationType == 'Fellowship'">
                                <div class="item-1" v-if="education.educationName">
                                    {{ education.educationName }}
                                </div>

                                <div class="item-2" v-if="education.educationSubject">
                                    {{ education.educationSubject }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="board-certifications" v-if="provider.board_certification && provider.board_certification.length">
                    <h3>Professional Credentials</h3>
                    <div class="wrapper">
                        <div class="group">
                            <div class="item" v-for="(boardCertification, index) in provider.board_certification" :key="index">
                                <div class="item-1" v-if="boardCertification.name">
                                    {{ boardCertification.name }}
                                </div>

                                <div class="item-2" v-if="boardCertification.certified">
                                    {{ boardCertification.certified }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="insurance" v-if="(provider.tiers.includes('qmg')) && (provider.insurances && provider.insurances.length)">
                    <h3>Insurance Accepted</h3>
                    <div class="wrapper">
                        <div class="group">
                            <div class="item">
                            <div class="item-1" v-for="(insurance, index) in provider.insurances" :key="index">
                                {{ insurance }}
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </section>
            <div class="disclaimer">
                Before scheduling an appointment, please contact your insurance company to ensure that The Queen's Health System location or provider you select is in your network. Your insurance company can provide you information about co-pays, co-insurance and deductibles that are your responsibility. If you schedule an appointment and your health insurance does not participate with The Queen's Health System, you may have higher out-of-pocket fees. If you do not have insurance, learn more about financial assistance opportunities by <a href="/pay-my-bill/" target="_blank">clicking here</a>.
            </div>
        </div>

        <script type="application/ld+json">
            [{
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [{
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Find a Doctor",
                    "item": "https://queens.org/find-a-doctor"
                },{
                    "@type": "ListItem",
                    "position": 2,
                    "name": "{{ provider.name }}",
                    "item": "https://www.queens.org/providers/{{ provider.id }}/{{ provider.name_for_url }}/"
                }]
            }]
        </script>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import ProviderFeatures from './ProviderFeatures.vue';
import ProviderSearch from './ProviderSearch.vue';

export default {
    components: { ProviderFeatures, ProviderSearch },

    props: {
        provider: { type: Object, required: true },
        backUrl: { type: String, default: '' },
    },

    data() {
        return {
            envId: "43d4fb8c-c50b-4b88-acc0-81faf7886114",
            style: "standard-light",
            show: 'map',
            map: null,
            markers: [],
            visibleMarkers: [],
            activeInfowindow: null,
            queryString: null,
            maxZoom: 10, // Default zoom level
        };
    },

    computed: {
        ...mapState(['locations', 'locationsLoaded', 'mapCenter']),

        campusMap() {
            const values = this.provider.locations.map(location => location.campus_map);
            return values.some(row => row.includes(1));
        },

        backButton() {
            if (!this.backUrl) return;
            return {
                url: this.backUrl + (this.queryString ? `?${this.queryString}` : ''),
                text: this.queryString ? 'Return to Listing' : 'Find a Doctor',
            };
        },

        medicalEducation() {
            return this.provider.education.filter(education => education.educationType === 'Medical Education' && (education.educationName || education.educationSubject));
        },

        internshipEducation() {
            return this.provider.education.filter(education => education.educationType === 'Internship' && (education.educationName || education.educationSubject));
        },

        residencyEducation() {
            return this.provider.education.filter(education => education.educationType === 'Residency' && (education.educationName || education.educationSubject));
        },

        fellowshipEducation() {
            return this.provider.education.filter(education => education.educationType === 'Fellowship' && (education.educationName || education.educationSubject));
        },
    },

    mounted() {
        this.initializeMap();
    },

    created() {
        this.setQueryString();
        this.$router.replace({ path: this.basePath });
        this.$store.dispatch('getLocationsById', "provider=" + this.provider.id);
    },

    watch: {
        locations(newLocations) {
            if (newLocations.length) {
                this.initializeMap();
            }
        }
    },

    methods: {
        async lookupPlaceID(placeId) {
            try {
                const response = await fetch(`https://freshpaint-hipaa-maps.com/places/v0/indexes/freshpaint/places/${placeId}?token=${this.envId}`, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });
                const data = await response.json();
                console.log("Place: ", data.Place);
                return data.Place;
            } catch (error) {
                console.error('Error fetching place:', error);
            }
        },

        newPopup(location) {
            const image = location.avatar ? `<img width="100%" src="${location.avatar}" alt="${location.building_name}" />` : '';
            const heading = location.building_name ? `<h3>${location.building_name}</h3>` : '';
            const address = location.address ? this.withLineBreaks(location.address) : '';
            const directionsAddress = location.address ? location.address.join('+') : '';
            const phone = location.phone ? location.phone : '';
            const phoneLink = location.phone_clickable ? `<a href="tel:${location.phone_clickable}">${phone}</a>` : phone;

            const contentString = `
                <div class="location">
                    <div class="text">
                        ${heading}
                        <p class="address">${address}<br /></p>
                        <p class="phone">${phoneLink}</p>
                        <div class="directions">
                            <a target="_blank" href="https://www.google.com/maps/dir/?api=1&destination=${directionsAddress}">
                                <i aria-hidden="true" class="fas fa-map-marker"></i>
                                <span>Directions</span>
                            </a>
                        </div>
                    </div>
                </div>`;

            return new maplibregl.Popup({ offset: 25 }).setHTML(contentString);
        },

        removeAllPopups() {
            const popups = document.getElementsByClassName("maplibregl-popup");
            while (popups.length) {
                popups[0].parentNode.removeChild(popups[0]);
            }
        },

        initializeMap() {
            if (!window.maplibregl) {
                console.error('maplibregl is not defined');
                return;
            }

            this.map = new maplibregl.Map({
                container: "map",
                style: `https://freshpaint-hipaa-maps.com/${this.envId}/${this.style}/style-descriptor`,
                center: [-157.0072, 20.3843], // Default center
                zoom: 6,
            });

            this.map.addControl(new maplibregl.NavigationControl(), "top-left");

            const bounds = new maplibregl.LngLatBounds();

            this.$store.state.locations.forEach(location => {
                console.log('Location data:', location);

                // Adjust for nested coordinates
                const lat = location.coordinates && typeof location.coordinates.lat === 'number' ? location.coordinates.lat : 0;
                const lng = location.coordinates && typeof location.coordinates.lng === 'number' ? location.coordinates.lng : 0;

                const markerData = {
                    LngLat: [lng, lat],
                    Header: location.building_name || 'No Name',
                    Href: location.url || '#',
                };

                const popup = this.newPopup(location);
                new maplibregl.Marker({ color: "#808080" })
                    .setLngLat(markerData.LngLat)
                    .setPopup(popup)
                    .addTo(this.map);

                bounds.extend(markerData.LngLat);
            });

            // Use fitBounds with max zoom level
            if (this.$store.state.locations.length > 1) {
                this.map.fitBounds(bounds, {
                    padding: 50,
                    maxZoom: this.maxZoom // Use the admin-specified zoom level
                });
            } else if (this.$store.state.locations.length === 1) {
                // For a single marker, set the center and prevent zooming too close
                const coords = this.$store.state.locations[0].coordinates;
                this.map.setCenter(coords);
                this.map.setZoom(this.maxZoom); // Set the zoom level from admin settings
            }
        },

        setQueryString() {
            const query = this.$route.query;
            this.queryString = Object.keys(query)
                .map(key => `${key}=${query[key] || ''}`)
                .join('&');
        },

        withLineBreaks(value) {
            return Array.isArray(value) ? value.join('<br />') : value;
        },
    },
};
</script>
